import { ColDef, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, blueButtonClass, grayButtonClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR1021_R01Store } from "./Store_PUR1021_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR1021_R01HeaderDto, defaultPUR1021_R01PurchaseDto, PUR1021_R01HeaderDto, PUR1021_R01PurchaseDto } from "./Dto_PUR1021_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1021_R01-purchase-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PUR1021_R01PurchaseDto> | null>;
	handlePUR1030ProcessClick: () => void;
}

export const PurchaseTable_PUR1021_R01 = (props: Props) => {

	const { gridRef, handlePUR1030ProcessClick } = props;

    const { 
		selectedPUR1021_R01,
        PUR1021_R01PurchaseList, setPUR1021_R01PurchaseList,
        deletedPUR1021_R01PurchaseList, setDeletedPUR1021_R01PurchaseList, addDeletedPUR1021_R01PurchaseList, removeDeletedPUR1021_R01PurchaseList, 
        selectedPUR1021_R01PurchaseList, setSelectedPUR1021_R01PurchaseList, addSelectedPUR1021_R01PurchaseList, removeSelectedPUR1021_R01PurchaseList,
        originalPUR1021_R01PurchaseList, setOriginalPUR1021_R01PurchaseList,
		purchaseColumnState, setPurchaseColumnState,
		purchaseScrollTop, setPurchaseScrollTop,
    } = usePUR1021_R01Store() as any;

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
    } = useGridSettingContent<PUR1021_R01PurchaseDto>({
		gridRef,
		key: 'childItemcd',
        gridData: PUR1021_R01PurchaseList,
        defaultGridData: defaultPUR1021_R01PurchaseDto,
        selectedRow: selectedPUR1021_R01PurchaseList,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		childItemcd: '품목코드',
		childItemnm: '품명',
		materialIntype: '규격',
		locationNm: '보관위치',
		childItemUsage: '소요량',
		MOQ: 'MOQ',
		childItemUnit: '자품목단위',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'childItemcd')(params), []);

    const baseColumnDefs: ColDef<PUR1021_R01PurchaseDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'childItemcd', field: 'childItemcd', width: 180,
			cellStyle: (p) => getCellStyle(p, 'childItemcd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'childItemnm', field: 'childItemnm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'childItemnm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'materialIntype', field: 'materialIntype', width: 60,
			cellStyle: (p) => getCellStyle(p, 'materialIntype'),
			headerClass: aggridHeaderClass,
			cellRenderer: (params: { data?: PUR1021_R01PurchaseDto }) => {
				if (params.data?.materialIntype === 'PURCHASE') {
					return <div>도급</div>;
				}
			},
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 70,
			cellStyle: (p) => getCellStyle(p, 'childItemUsage'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '총소요량', colId: 'totalQty', field: 'totalQty', width: 80,
			cellStyle: (p) => getCellStyle(p, 'totalQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: 'MOQ', colId: 'moqQty', field: 'moqQty', width: 70,
			cellStyle: (p) => getCellStyle(p, 'moqQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '현재고총량', colId: 'stockQty', field: 'stockQty', width: 90,
			cellStyle: (p) => getCellStyle(p, 'stockQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '발주필요량', colId: 'requiredPurchaseQty', field: 'requiredPurchaseQty', width: 90,
			cellStyle: (p) => getCellStyle(p, 'requiredPurchaseQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '자품목단위', colId: 'childItemUnit', field: 'childItemUnit', width: 90,
			cellStyle: (p) => getCellStyle(p, 'childItemUnit'),
			headerClass: aggridHeaderClass,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
    ], [getCellStyle, unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

    const columnDefs: ColDef<PUR1021_R01PurchaseDto>[] = useMemo(() => {
		if (purchaseColumnState === null) {
			return baseColumnDefs;
		}
        return GridCustomColumnDefs<PUR1021_R01PurchaseDto>({
			baseColumnDefs,
			columnState: purchaseColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [purchaseColumnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
    const handleRowClick = useCallback((e: RowClickedEvent<PUR1021_R01PurchaseDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - childItemcd 사용
		const currentKey = rowData.childItemcd;
        const selectedKey = selectedPUR1021_R01PurchaseList;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
            setSelectedPUR1021_R01PurchaseList([]);
		} else {
            setSelectedPUR1021_R01PurchaseList([currentKey]);
		}
    }, [selectedPUR1021_R01PurchaseList, setSelectedPUR1021_R01PurchaseList]);

	
    const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
        childItemcd: PUR1021_R01PurchaseList.length.toLocaleString() + "건",
		moqQty: '',
		requiredPurchaseQty: PUR1021_R01PurchaseList.reduce((sum: number, item: PUR1021_R01PurchaseDto) => sum + (Number(item.requiredPurchaseQty) || 0), 0),
		totalQty: PUR1021_R01PurchaseList.reduce((sum: number, item: PUR1021_R01PurchaseDto) => sum + (Number(item.totalQty) || 0), 0),
		stockQty: PUR1021_R01PurchaseList.reduce((sum: number, item: PUR1021_R01PurchaseDto) => sum + (Number(item.stockQty) || 0), 0),
		childItemUsage: '',
    }]), [PUR1021_R01PurchaseList]);

	useEffect(() => { ensureSelectedRow(); }, [PUR1021_R01PurchaseList, selectedPUR1021_R01PurchaseList]);

	const isDisabled = useMemo(() => {
		return PUR1021_R01PurchaseList.length < 1;
	}, [PUR1021_R01PurchaseList]);

	return (
		<div className="flex flex-col gap-[4px] w-full h-full">
			<GridContextWrapper<PUR1021_R01PurchaseDto>
				enableCellCalculation
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				initSelected={() => setSelectedPUR1021_R01PurchaseList([])}
				defaultRowData={defaultPUR1021_R01PurchaseDto}
				baseColumnDefs={purchaseColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setPurchaseColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				
				<div className="flex justify-end">
					
					<button
							onClick={handlePUR1030ProcessClick}
							className={`${ 
										isDisabled ? grayButtonClass : blueButtonClass
									} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px] mb-[4px]`}
							disabled={isDisabled}
							title={isDisabled ? "수주 처리할 품목을 선택해주세요" : "선택된 품목을 수주로 전환"}
						>
							발주 처리
					</button>
				</div>
				<Grid_Touch<PUR1021_R01PurchaseDto>
					ref={gridRef}
					rowData={paddedGridData}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowId}
					
					rowClassRules={createRowClassRules<PUR1021_R01PurchaseDto>({
						idKey: 'childItemcd',
						selectedList: selectedPUR1021_R01PurchaseList,
						deletedList: [],
						
					})}
					onRowClicked={handleRowClick}
					onCellKeyDown={undefined}
					pinnedBottomRowData={pinnedBottomRow}	
					
					// Grid_Select 공통 로직 처리를 위한 props 전달
					setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
					setColumnState={setPurchaseColumnState}			//컬럼 상태 저장
					scrollTop={purchaseScrollTop}
					setScrollTop={setPurchaseScrollTop}
				/>
			</GridContextWrapper>
		</div>
	)
}
